import { type FC, useCallback, useState, useEffect } from 'react';
import styles from './Header.module.scss';
import { Navigation } from './components/Navigation';
import { NotificationBell } from './components/NotificationBell';
import { NewSitePopup } from '../NewSitePopup';
import { SitesDropdown } from 'src/redesign/features/Header/components/Dropdown';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { SwitchButton } from 'src/redesign/components/SwitchButton';
import { accountActions } from 'src/redesign/features/Account/store/reducer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useWhiteLabel } from 'src/redesign/features/WhiteLabel';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { updateAccount } from 'src/redesign/features/Account/store/actions';

export const Header: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const account = useAppSelector(accountSelector);
  const [isAddSitePopupVisible, setIsAddSitePopupVisible] = useState(false);
  const { logoUrl, siteName } = useWhiteLabel();
  const handleOnAddSiteClick = () => {
    setIsAddSitePopupVisible(true);
  };
  const handleAddSitePopupClose = () => {
    setIsAddSitePopupVisible(false);
  };

  const handleSwitchRole = useCallback(
    async (role: string) => {
      if (role === 'My Tickets') {
        // Update account to set is_event_organizer to false
        dispatch(accountActions.switchRole('attendee'));
        await dispatch(updateAccount({ is_event_organizer: false }));

        history.replace('/attendee-dashboard');
        return;
      }
      // Update account to set is_event_organizer to true
      dispatch(accountActions.switchRole('host'));
      await dispatch(updateAccount({ is_event_organizer: true }));

      history.replace('/events');
    },
    [dispatch, history],
  );

  // Determine current switch value based on is_event_organizer from account
  const currentSwitchValue = account?.is_event_organizer ? 'Host' : 'My Tickets';

  // Sync route with account's is_event_organizer on initial load
  useEffect(() => {
    if (!account) return;

    const isOnAttendeeDashboard = location.pathname.includes('attendee-dashboard');
    const shouldBeOnAttendeeDashboard = !account.is_event_organizer;

    // If there's a mismatch between account state and current route, navigate to correct route
    if (shouldBeOnAttendeeDashboard && !isOnAttendeeDashboard) {
      // Account says attendee but we're on host view
      dispatch(accountActions.switchRole('attendee'));
      history.replace('/attendee-dashboard');
    } else if (!shouldBeOnAttendeeDashboard && isOnAttendeeDashboard) {
      // Account says host but we're on attendee view
      dispatch(accountActions.switchRole('host'));
      history.replace('/events');
    }
  }, [account, location.pathname, dispatch, history]);

  return (
    <header className={styles.header}>
      <div className={styles.content}>
        <div className={styles.leftContent}>
          <Link to="/">
            <img
              className={styles.logo}
              src={logoUrl || 'https://cdn.ticketspot.io/logo.png'}
              alt={siteName || 'logo'}
            />
          </Link>
          <SitesDropdown onAddSiteClick={handleOnAddSiteClick} />
        </div>

        <div className={styles.switchContainer} style={{ margin: '0 12px' }}>
          <SwitchButton
            onChange={handleSwitchRole}
            items={['Host', 'My Tickets']}
            value={currentSwitchValue}
          />
        </div>

        <div className={styles.rightContent}>
          <div className={styles.hideOnMobile}>
            <NotificationBell />
          </div>
          <Navigation />
        </div>
      </div>
      <NewSitePopup isOpen={isAddSitePopupVisible} onClose={handleAddSitePopupClose} />
    </header>
  );
};
